export const ADMIN_ROLES = {
  FOUNDER: 'FOUNDER',
  HOD: 'HOD',
  SENIOR_MANAGER: 'SENIOR_MANAGER',
  MANAGER: 'MANAGER',
  EXECUTIVE: 'EXECUTIVE',
  TRAINEE: 'TRAINEE',
} as const;

export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];

export const ADMIN_DEPARTMENTS = {
  SALES: 'SALES',
  MARKETING: 'MARKETING',
  HR: 'HR',
  ADMIN_BACK_OFFICE: 'ADMIN_BACK_OFFICE',
  IT: 'IT',
  SERVICE: 'SERVICE',
  FINANCE_ACCOUNTS: 'FINANCE_ACCOUNTS',
} as const;

export type AdminDepartment =
  (typeof ADMIN_DEPARTMENTS)[keyof typeof ADMIN_DEPARTMENTS];

export const TRAINEE_DEPARTMENTS = ADMIN_DEPARTMENTS;

export type TraineeDepartment = AdminDepartment;

export type AdminModule =
  | 'dashboard'
  | 'enquiries'
  | 'cancelled'
  | 'payments'
  | 'customers'
  | 'technicians'
  | 'attendance'
  | 'helpers'
  | 'technician_pairing'
  | 'assign_technician'
  | 'bookings'
  | 'promotions'
  | 'leads'
  | 'services'
  | 'coupons'
  | 'banners'
  | 'products'
  | 'material'
  | 'taxation'
  | 'tools'
  | 'amc'
  | 'copper_piping'
  | 'consultancy'
  | 'purchase'
  | 'sell_old_ac'
  | 'utility'
  | 'admin_staff'
  | 'partner_onboarding'
  | 'general';

export interface AdminPermissions {
  role: AdminRole;
  department: AdminDepartment;
  canDelete: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canView: boolean;
  useCustomPermissions?: boolean;
  modules: {
    view: string[];
    create: string[];
    edit: string[];
    delete: string[];
  };
}

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';

export const MODULE_LABELS: Record<AdminModule, string> = {
  dashboard: 'Dashboard',
  enquiries: 'Enquiries',
  cancelled: 'Cancelled Bookings',
  payments: 'Payments',
  customers: 'Customers',
  technicians: 'Technicians',
  attendance: 'Attendance',
  helpers: 'Helpers',
  technician_pairing: 'Technician Pairing',
  assign_technician: 'Assign Technician',
  bookings: 'Bookings',
  promotions: 'Promotions',
  leads: 'Leads',
  services: 'Services',
  coupons: 'Coupons',
  banners: 'Banners',
  products: 'Products',
  material: 'Material',
  taxation: 'Taxation',
  tools: 'Tools',
  amc: 'AMC',
  copper_piping: 'Copper Piping',
  consultancy: 'Consultancy',
  purchase: 'Purchase',
  sell_old_ac: 'Sell Old AC',
  utility: 'Utility',
  admin_staff: 'Admin Roles',
  partner_onboarding: 'Channel Partners',
  general: 'General / Zones',
};

export const ALL_MODULES: AdminModule[] = Object.keys(
  MODULE_LABELS,
) as AdminModule[];

export const ROLE_LABELS: Record<AdminRole, string> = {
  FOUNDER: 'Founder',
  HOD: 'Head of Department',
  SENIOR_MANAGER: 'Senior Manager',
  MANAGER: 'Manager',
  EXECUTIVE: 'Executive',
  TRAINEE: 'Trainee',
};

export const DEPARTMENT_LABELS: Record<AdminDepartment, string> = {
  SALES: 'Sales',
  MARKETING: 'Marketing',
  HR: 'HR',
  ADMIN_BACK_OFFICE: 'Admin & Back Office',
  IT: 'IT',
  SERVICE: 'Service',
  FINANCE_ACCOUNTS: 'Finance & Accounts',
};

export const DEPARTMENT_OPTIONS = Object.values(ADMIN_DEPARTMENTS);

export const ROLE_COLORS: Record<AdminRole, string> = {
  FOUNDER: '#f59e0b',
  HOD: '#8b5cf6',
  SENIOR_MANAGER: '#3b82f6',
  MANAGER: '#10b981',
  EXECUTIVE: '#6366f1',
  TRAINEE: '#64748b',
};

const allows = (allowedModules: string[], moduleName: string) => {
  if (!allowedModules?.length) return false;
  if (allowedModules.includes('*')) return true;
  return allowedModules.includes(moduleName);
};

const ROLE_RULES: Record<
  AdminRole,
  { view: string[]; create: string[]; edit: string[]; delete: string[] }
> = {
  FOUNDER: { view: ['*'], create: ['*'], edit: ['*'], delete: ['*'] },
  HOD: { view: ['*'], create: ['*'], edit: ['*'], delete: [] },
  SENIOR_MANAGER: { view: ['*'], create: ['*'], edit: ['*'], delete: [] },
  MANAGER: {
    view: [
      'enquiries',
      'bookings',
      'technicians',
      'attendance',
      'helpers',
      'technician_pairing',
      'assign_technician',
      'leads',
      'consultancy',
      'customers',
      'payments',
      'purchase',
      'amc',
      'copper_piping',
      'sell_old_ac',
      'partner_onboarding',
      'general',
    ],
    create: ['bookings', 'enquiries', 'assign_technician', 'partner_onboarding'],
    edit: [
      'bookings',
      'enquiries',
      'assign_technician',
      'technicians',
      'attendance',
      'helpers',
      'technician_pairing',
      'partner_onboarding',
    ],
    delete: [],
  },
  EXECUTIVE: { view: ['*'], create: [], edit: [], delete: [] },
  TRAINEE: { view: [], create: [], edit: [], delete: [] },
};

const MARKETING_SALES_TRAINEE_RULES = {
  view: ['leads', 'consultancy', 'general'],
  create: [],
  edit: [],
  delete: [],
};

const SERVICE_TRAINEE_RULES = {
  view: [
    'enquiries',
    'technicians',
    'attendance',
    'helpers',
    'technician_pairing',
    'assign_technician',
    'general',
  ],
  create: ['assign_technician'],
  edit: ['assign_technician', 'attendance', 'helpers', 'technician_pairing'],
  delete: [],
};

const GENERAL_TRAINEE_RULES = {
  view: ['general'],
  create: [],
  edit: [],
  delete: [],
};

const FINANCE_TRAINEE_RULES = {
  view: ['payments', 'general'],
  create: [],
  edit: [],
  delete: [],
};

const TRAINEE_RULES: Record<
  AdminDepartment,
  { view: string[]; create: string[]; edit: string[]; delete: string[] }
> = {
  MARKETING: MARKETING_SALES_TRAINEE_RULES,
  SALES: MARKETING_SALES_TRAINEE_RULES,
  SERVICE: SERVICE_TRAINEE_RULES,
  HR: GENERAL_TRAINEE_RULES,
  ADMIN_BACK_OFFICE: GENERAL_TRAINEE_RULES,
  IT: GENERAL_TRAINEE_RULES,
  FINANCE_ACCOUNTS: FINANCE_TRAINEE_RULES,
};

const LEGACY_DEPARTMENT_ALIASES: Record<string, AdminDepartment> = {
  MARKETING_SALES: ADMIN_DEPARTMENTS.MARKETING,
  SERVICE_OPS: ADMIN_DEPARTMENTS.SERVICE,
};

export const normalizeDepartment = (
  department?: string | null,
): AdminDepartment => {
  if (!department) return ADMIN_DEPARTMENTS.SERVICE;

  const value = String(department)
    .trim()
    .toUpperCase()
    .replace(/&/g, 'AND')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (LEGACY_DEPARTMENT_ALIASES[value]) {
    return LEGACY_DEPARTMENT_ALIASES[value];
  }

  if (Object.values(ADMIN_DEPARTMENTS).includes(value as AdminDepartment)) {
    return value as AdminDepartment;
  }

  return ADMIN_DEPARTMENTS.SERVICE;
};

export const getDepartmentLabel = (department?: string | null) => {
  if (!department) return '—';
  return DEPARTMENT_LABELS[normalizeDepartment(department)];
};

const getRoleRules = (role: AdminRole, department: AdminDepartment | null) => {
  if (role === ADMIN_ROLES.TRAINEE) {
    return TRAINEE_RULES[normalizeDepartment(department)] || GENERAL_TRAINEE_RULES;
  }
  return ROLE_RULES[role] || ROLE_RULES.EXECUTIVE;
};

export const normalizeDepartments = (
  departments?: (string | null | undefined)[] | null,
  fallbackDepartment?: string | null,
): AdminDepartment[] => {
  const list = Array.isArray(departments) ? departments : [];
  const normalized = Array.from(new Set(list.map((d) => normalizeDepartment(d))));
  if (normalized.length > 0) return normalized;
  return [normalizeDepartment(fallbackDepartment)];
};

// TRAINEE is the only role whose default rules are department-keyed, so
// multiple departments only change behavior for that role — mirrors the
// backend's getRoleRulesForAdmin in adminRoles.js.
export const getRoleRulesForAdmin = (
  role: AdminRole,
  departments?: (string | null | undefined)[] | null,
) => {
  if (role !== ADMIN_ROLES.TRAINEE) {
    return ROLE_RULES[role] || ROLE_RULES.EXECUTIVE;
  }

  const deptList = normalizeDepartments(departments, departments?.[0]);
  const ruleSets = deptList.map((dept) => TRAINEE_RULES[dept] || GENERAL_TRAINEE_RULES);

  const unionAction = (action: PermissionAction) => {
    const merged = new Set<string>();
    for (const rules of ruleSets) {
      const list = rules[action] || [];
      if (list.includes('*')) return ['*'];
      list.forEach((m) => merged.add(m));
    }
    return Array.from(merged);
  };

  return {
    view: unionAction('view'),
    create: unionAction('create'),
    edit: unionAction('edit'),
    delete: unionAction('delete'),
  };
};

export const buildPermissionsFromDepartments = (
  role: AdminRole,
  departments?: (string | null | undefined)[] | null,
): AdminPermissions => {
  const rules = getRoleRulesForAdmin(role, departments);
  const primaryDepartment = normalizeDepartments(departments, departments?.[0])[0];
  return buildFromModules(role, primaryDepartment, rules, false);
};

const buildFromModules = (
  role: AdminRole,
  department: AdminDepartment | null,
  modules: AdminPermissions['modules'],
  useCustomPermissions = true,
): AdminPermissions => ({
  role,
  department: normalizeDepartment(department),
  canDelete: allows(modules.delete, '*') || modules.delete.length > 0,
  canCreate: allows(modules.create, '*') || modules.create.length > 0,
  canEdit: allows(modules.edit, '*') || modules.edit.length > 0,
  canView: allows(modules.view, '*') || modules.view.length > 0,
  useCustomPermissions,
  modules: {
    view: modules.view || [],
    create: modules.create || [],
    edit: modules.edit || [],
    delete: modules.delete || [],
  },
});

export const buildPermissionsFromRole = (
  role: AdminRole,
  department?: AdminDepartment | null,
): AdminPermissions => {
  const rules = getRoleRules(role, department ?? null);
  return buildFromModules(role, department ?? null, rules, false);
};

export interface CustomPermissionSource {
  useCustomPermissions?: boolean;
  customPermissions?: { modules: AdminPermissions['modules'] };
}

export const resolvePermissions = (
  role: AdminRole,
  department: AdminDepartment | null,
  stored?: CustomPermissionSource | AdminPermissions | null,
): AdminPermissions => {
  if (stored && 'useCustomPermissions' in stored && stored.useCustomPermissions) {
    const customSource = stored as CustomPermissionSource;
    const modules =
      customSource.customPermissions?.modules ||
      (stored as AdminPermissions).modules;
    if (modules) {
      return buildFromModules(role, department, modules, true);
    }
  }
  return buildPermissionsFromRole(role, department);
};

export const moduleHasAction = (
  modules: string[],
  module: string,
): boolean => allows(modules, module);

export const toggleModuleAction = (
  modules: string[],
  module: AdminModule,
): string[] => {
  if (modules.includes('*')) {
    return ALL_MODULES.filter((m) => m !== module);
  }
  if (modules.includes(module)) {
    return modules.filter((m) => m !== module);
  }
  const next = [...modules, module];
  if (next.length === ALL_MODULES.length) return ['*'];
  return next;
};

export const cloneModules = (
  modules: AdminPermissions['modules'],
): AdminPermissions['modules'] => ({
  view: [...(modules.view || [])],
  create: [...(modules.create || [])],
  edit: [...(modules.edit || [])],
  delete: [...(modules.delete || [])],
});

export const countModuleAccess = (permissions: AdminPermissions): number => {
  const unique = new Set<string>();
  (['view', 'create', 'edit', 'delete'] as PermissionAction[]).forEach(
    (action) => {
      const list = permissions.modules[action];
      if (list.includes('*')) {
        ALL_MODULES.forEach((m) => unique.add(`${m}:${action}`));
      } else {
        list.forEach((m) => unique.add(`${m}:${action}`));
      }
    },
  );
  return unique.size;
};

export const getResolvedPermissions = (authState: {
  role: AdminRole | null;
  department: AdminDepartment | null;
  permissions: AdminPermissions | null;
  userType: string | null;
  useCustomPermissions?: boolean;
  customPermissions?: { modules: AdminPermissions['modules'] } | null;
}): AdminPermissions => {
  const role = normalizeRole(authState.role, authState.userType);
  const department = normalizeDepartment(authState.department);

  if (authState.useCustomPermissions) {
    const modules =
      authState.customPermissions?.modules || authState.permissions?.modules;
    if (modules) {
      return resolvePermissions(role, department, {
        useCustomPermissions: true,
        customPermissions: { modules },
      });
    }
  }

  if (authState.permissions?.useCustomPermissions) {
    return authState.permissions;
  }

  return resolvePermissions(role, department, authState.permissions);
};

export const normalizeRole = (role?: string | null, type?: number | string | null): AdminRole => {
  if (!role) {
    if (Number(type) === 5) return ADMIN_ROLES.FOUNDER;
    return ADMIN_ROLES.EXECUTIVE;
  }
  const value = String(role).trim().toUpperCase().replace(/\s+/g, '_');
  if (value === 'ADMIN' || value === 'SUPER_ADMIN') return ADMIN_ROLES.FOUNDER;
  if (Object.values(ADMIN_ROLES).includes(value as AdminRole)) {
    return value as AdminRole;
  }
  return ADMIN_ROLES.EXECUTIVE;
};

const FOUNDER_ONLY_MODULES: AdminModule[] = ['admin_staff'];

// Fine-grained, per-feature permissions inside a module — a second,
// optional layer on top of the view/create/edit/delete matrix. Mirrors
// MODULE_FEATURES / MODULE_FEATURE_FALLBACK_ACTION / canPerformFeature in
// acdoctorserverapp/src/Config/adminRoles.js exactly.
export const MODULE_FEATURES: Partial<Record<AdminModule, Record<string, string>>> = {
  bookings: {
    assign_technician: 'Assign Technician',
    cancel_booking: 'Cancel Booking',
    view_invoice: 'View Invoice',
  },
  admin_staff: {
    change_role: 'Change Role',
    grant_permissions: 'Grant Permissions',
    toggle_active: 'Activate / Deactivate',
    delete_staff: 'Delete Staff',
  },
  technicians: {
    mark_attendance: 'Mark Attendance',
    pair_helper: 'Pair / Unpair Helper',
    assign_tool: 'Assign Tool',
  },
};

const MODULE_FEATURE_FALLBACK_ACTION: Partial<Record<AdminModule, Record<string, PermissionAction>>> = {
  bookings: { assign_technician: 'edit', cancel_booking: 'delete', view_invoice: 'view' },
  admin_staff: { change_role: 'edit', grant_permissions: 'edit', toggle_active: 'edit', delete_staff: 'delete' },
  technicians: { mark_attendance: 'edit', pair_helper: 'edit', assign_tool: 'edit' },
};

export type FeatureOverrides = Record<string, string[]>;

export const canPerformFeature = (
  permissions: AdminPermissions | null | undefined,
  customFeatures: FeatureOverrides | null | undefined,
  moduleName: AdminModule,
  featureKey: string,
): boolean => {
  if (!permissions) return false;
  if (permissions.role === ADMIN_ROLES.FOUNDER) return true;

  const overrides = customFeatures?.[moduleName];
  if (Array.isArray(overrides)) return overrides.includes(featureKey);

  const fallbackAction = MODULE_FEATURE_FALLBACK_ACTION[moduleName]?.[featureKey] || 'edit';
  return canAccessModule(permissions, moduleName, fallbackAction);
};

export const canAccessModule = (
  permissions: AdminPermissions | null | undefined,
  moduleName: AdminModule,
  action: PermissionAction = 'view',
) => {
  if (!permissions) return false;
  if (
    !permissions.useCustomPermissions &&
    FOUNDER_ONLY_MODULES.includes(moduleName) &&
    permissions.role !== ADMIN_ROLES.FOUNDER
  ) {
    return false;
  }
  return allows(permissions.modules[action] || [], moduleName);
};

const LANDING_ROUTE_CANDIDATES: { path: string; module: AdminModule }[] = [
  { path: '/home', module: 'dashboard' },
  { path: '/enquires', module: 'enquiries' },
  { path: '/leads', module: 'leads' },
  { path: '/request/free-consultancy', module: 'consultancy' },
  { path: '/bookings', module: 'bookings' },
  { path: '/technicians/attendance', module: 'attendance' },
  { path: '/technicians', module: 'technicians' },
  { path: '/payments', module: 'payments' },
  { path: '/customers', module: 'customers' },
  { path: '/purchase', module: 'purchase' },
  { path: '/request/amc', module: 'amc' },
  { path: '/zones', module: 'general' },
];

export const getFirstAllowedRoute = (
  permissions: AdminPermissions,
): string => {
  for (const { path, module } of LANDING_ROUTE_CANDIDATES) {
    if (canAccessModule(permissions, module, 'view')) return path;
  }
  return '/home';
};

export const canCreate = (permissions?: AdminPermissions | null) =>
  Boolean(permissions?.canCreate);

export const canEdit = (permissions?: AdminPermissions | null) =>
  Boolean(permissions?.canEdit);

export const canDelete = (permissions?: AdminPermissions | null) =>
  Boolean(permissions?.canDelete);

export const ROUTE_MODULE_MAP: Record<string, AdminModule> = {
  '/home': 'dashboard',
  '/enquires': 'enquiries',
  '/cancelled': 'cancelled',
  '/payments': 'payments',
  '/customers': 'customers',
  '/technicians': 'technicians',
  '/bookings': 'bookings',
  '/promotions': 'promotions',
  '/leads': 'leads',
  '/manage/services': 'services',
  '/manage/coupons': 'coupons',
  '/manage/banners': 'banners',
  '/Product': 'products',
  '/add-product': 'products',
  '/products/edit': 'products',
  '/productdetails': 'products',
  '/manage/material': 'material',
  '/manage/taxation': 'taxation',
  '/manage/tools': 'tools',
  '/manage/leaves': 'technicians',
  '/zones': 'general',
  '/request/amc': 'amc',
  '/request/copperpiping': 'copper_piping',
  '/request/free-consultancy': 'consultancy',
  '/purchase': 'purchase',
  '/request/sell-old-ac': 'sell_old_ac',
  '/utility/error-codes': 'utility',
  '/utility/tonnage-calculator': 'utility',
  '/utility/comparison': 'utility',
  '/admin-staff': 'admin_staff',
  '/channel-partners': 'partner_onboarding',
  '/channel-partners/identity': 'partner_onboarding',
  '/amc': 'amc',
  '/technicians/attendance': 'attendance',
  '/technicians/new-join': 'technicians',
  '/technicians/acd-technicians': 'technicians',
  '/technicians/acd-helper': 'helpers',
  '/technicians/contractor': 'technicians',
  '/technicians/tool-request': 'tools',
  '/bookings/create': 'bookings',
  '/bookings/edit': 'bookings',
  '/bookings/assign': 'assign_technician',
  '/manage/services/create': 'services',
  '/purchase/lead-details': 'purchase',
  '/request/detail': 'sell_old_ac',
};

export const getModuleForPath = (pathname: string): AdminModule => {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const entries = Object.entries(ROUTE_MODULE_MAP).sort(
    (a, b) => b[0].length - a[0].length,
  );
  const match = entries.find(([route]) =>
    normalized.toLowerCase().startsWith(route.toLowerCase()),
  );
  return match?.[1] || 'general';
};

export const MENU_ACCESS: Record<AdminModule, AdminModule> = {
  dashboard: 'dashboard',
  enquiries: 'enquiries',
  cancelled: 'cancelled',
  payments: 'payments',
  customers: 'customers',
  technicians: 'technicians',
  attendance: 'attendance',
  helpers: 'helpers',
  technician_pairing: 'technician_pairing',
  assign_technician: 'assign_technician',
  bookings: 'bookings',
  promotions: 'promotions',
  leads: 'leads',
  services: 'services',
  coupons: 'coupons',
  banners: 'banners',
  products: 'products',
  material: 'material',
  taxation: 'taxation',
  tools: 'tools',
  amc: 'amc',
  copper_piping: 'copper_piping',
  consultancy: 'consultancy',
  purchase: 'purchase',
  sell_old_ac: 'sell_old_ac',
  utility: 'utility',
  admin_staff: 'admin_staff',
  partner_onboarding: 'partner_onboarding',
  general: 'general',
};
